import type { ApolloCache, ApolloClient } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';

import { CONTENT_LABEL_ASSIGNMENTS_QUERY } from './queryContentLabelAssignments';
import { CONTENT_LABEL_STAGE_COUNTS_QUERY } from './queryContentLabelStageCounts';
import { CONTENTS_BY_ASSIGNMENT_STAGE_QUERIES } from './queryContentsByAssignmentStage';

/**
 * What every Content Labels v2 mutation shares.
 */

/**
 * Label mutations report their own failures — the UI surfaces each one exactly
 * once, where the user acted — so they opt out of the generic toast
 * `errorLink` in `src/apollo.ts` would otherwise add on top.
 */
export const CONTENT_LABEL_MUTATION_CONTEXT = { skipGlobalErrorToast: true } as const;

export function contentLabelCategoryCacheId(
  cache: ApolloCache<unknown>,
  contentLabelCategoryId: string,
): string | undefined {
  return cache.identify({ __typename: 'ContentLabelCategory', contentLabelCategoryId });
}

/**
 * Drops every cached `contentsByAssignmentStage(...)` read of ONE category —
 * every portfolio scope, the counts and every page alike (evicting by field name
 * removes all argument variants).
 *
 * Evicting rather than patching: which stage a content lands in is computed
 * server-side from every label's rule and every manual pick, so the client cannot
 * know where a row moves. A read that is NOT on screen then finds its data
 * missing and asks the server when it is next shown.
 *
 * On its own this is not enough for the reads that ARE on screen: Apollo does
 * not refetch a query while it already has a request in flight, and it finds
 * nothing to evict when an earlier mutation already did — so an answer computed
 * before this mutation (the previous mutation's refetch, a slow first load)
 * would land last and stay. Hence `activeStageReads` next to it, always.
 */
export function evictCategoryStages(cache: ApolloCache<unknown>, contentLabelCategoryId: string): void {
  cache.evict({
    id: contentLabelCategoryCacheId(cache, contentLabelCategoryId),
    fieldName: 'contentsByAssignmentStage',
  });
}

/** Every document that reads `contentsByAssignmentStage`, by operation name. */
const STAGE_READ_OPERATIONS = new Set(
  [CONTENT_LABEL_STAGE_COUNTS_QUERY, ...Object.values(CONTENTS_BY_ASSIGNMENT_STAGE_QUERIES)].map((document) =>
    getOperationName(document),
  ),
);

/**
 * The stage reads on screen right now — the coverage counts, the open stage
 * tables — as a `refetchQueries` list, for every mutation that moves contents
 * between stages. Each is asked again as a request of its own (deduplication is
 * off for stage reads, see `STAGE_READ_CONTEXT`), and Apollo keeps only the
 * latest request's answer per query, so whatever was already in flight can no
 * longer be the last word.
 *
 * Returned from `refetchQueries` as a FUNCTION of the result, which is what
 * makes it work: Apollo calls it only for a successful answer, starts the
 * refetches right after the `update` effects and before it re-delivers the
 * evicted reads (which then find a request in flight and send no second one),
 * and does not make the mutation wait for them — that would take
 * `awaitRefetchQueries` or an `onQueryUpdated` callback, and turn a failed
 * refetch into a failed save. Only reads that are on screen are named, so
 * Apollo has no unknown query to warn about.
 */
export function activeStageReads(client: ApolloClient<unknown>): string[] {
  return activeReads(client, STAGE_READ_OPERATIONS);
}

/**
 * Every document that reads a content's per-category label state OUTSIDE the
 * stage tables, by operation name. One today: the portfolio Content tab's
 * `listContentsByIds` page read.
 */
const ASSIGNMENT_READ_OPERATIONS = new Set(
  [CONTENT_LABEL_ASSIGNMENTS_QUERY].map((document) => getOperationName(document)),
);

/**
 * The assignment reads on screen right now, as a `refetchQueries` list.
 *
 * THE REFETCH IS THE WHOLE MECHANISM HERE — there is no eviction half, and the
 * omission is deliberate rather than forgotten. `evictCategoryStages` works
 * because `contentsByAssignmentStage` hangs off a KEYED `ContentLabelCategory`
 * entity, so one `cache.evict` reaches every argument variant of it at once.
 * `listContentsByIds` is a ROOT field whose arguments are the page's content
 * ids: there is no entity to evict (`ContentV2` is unkeyed on purpose), and
 * evicting the root field by name would drop every page ever read, including
 * the ones a reader might page back to — for no gain, because the page on
 * screen is refetched anyway and the pages off screen are re-read when they are
 * next shown.
 *
 * Which category a content lands in after a write is computed server-side, so
 * there is nothing to patch either; asking again is the only honest answer.
 *
 * Deduplication is off for this read too (`STAGE_READ_CONTEXT`), so the refetch
 * is a request of its own and cannot join one computed before the write.
 */
export function activeAssignmentReads(client: ApolloClient<unknown>): string[] {
  return activeReads(client, ASSIGNMENT_READ_OPERATIONS);
}

/** The subset of `operations` that is mounted right now, de-duplicated. */
function activeReads(client: ApolloClient<unknown>, operations: Set<string>): string[] {
  const onScreen = [...client.getObservableQueries('active').values()].map((query) => query.queryName);
  return [...new Set(onScreen.filter((name) => operations.has(name)))];
}

/**
 * Every read a manual assignment or removal invalidates: the stage tables and
 * counts in Settings, and the portfolio Content tab's cells. Both surfaces can
 * be open in two tabs at once, so this is a union, never a choice.
 */
export function activeLabelAssignmentReads(client: ApolloClient<unknown>): string[] {
  return [...new Set([...activeStageReads(client), ...activeAssignmentReads(client)])];
}
